import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiServiceInterface } from '@hutsix/ngxh6';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import packageInfo from '../../../../package.json';

export interface SupportTicket {
    issueKey: string;
    summary: string;
    description: string;
    status: string;
    priority: string;
    createdDate: string;
    updatedDate: string;
    reporter: { displayName: string; emailAddress: string };
    comments?: SupportTicketComment[];
}

export interface SupportTicketComment {
    id: string;
    body: string;
    author: { displayName: string };
    created: string;
    public: boolean;
}

export interface CreateTicketPayload {
    summary: string;
    description: string;
    priority?: string;
}

@Injectable({ providedIn: 'root' })
export class SupportService {
    private readonly enabled: boolean;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        private http: HttpClient,
    ) {
        const supportConfig = (environment as any).support;
        this.enabled = supportConfig?.enabled ?? false;
    }

    get isEnabled(): boolean {
        return this.enabled;
    }

    listTickets(limit = 20, pageToken?: string, displayErrors = true): Observable<any> {
        const tokenParam = pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '';
        return this.api
            .get({
                url: `/api/support/tickets?limit=${limit}${tokenParam}`,
                useCache: false,
                displayErrors,
            })
            .pipe(map((res: any) => res.data ?? res));
    }

    createTicket(payload: CreateTicketPayload): Observable<any> {
        return this.api
            .post({
                url: '/api/support/tickets',
                data: {
                    ...payload,
                    appVersion: packageInfo.version,
                    appEnv: environment.env,
                    pagePath: window.location.pathname,
                },
                displayErrors: true,
            })
            .pipe(map((res: any) => res.data ?? res));
    }

    getTicket(issueKey: string): Observable<any> {
        return this.api
            .get({
                url: `/api/support/tickets/${issueKey}`,
                useCache: false,
                displayErrors: true,
            })
            .pipe(map((res: any) => res.data ?? res));
    }

    addComment(issueKey: string, body: string): Observable<any> {
        return this.api
            .post({
                url: `/api/support/tickets/${issueKey}/comments`,
                data: { body },
                displayErrors: true,
            })
            .pipe(map((res: any) => res.data ?? res));
    }

    confirmTicket(issueKey: string): Observable<any> {
        return this.api
            .post({
                url: `/api/support/tickets/${issueKey}/confirm`,
                data: {},
                displayErrors: true,
            })
            .pipe(map((res: any) => res.data ?? res));
    }

    uploadAttachment(issueKey: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        // Use HttpClient directly — the ApiService sets Content-Type: application/json
        // which breaks multipart FormData uploads
        return this.http.post(
            `${environment.apiUrl}/api/support/tickets/${issueKey}/attachments`,
            formData,
        );
    }

    // ─── Admin ───

    checkConnection(): Observable<{ connected: boolean }> {
        return this.api
            .get({
                url: '/api/support/admin/status',
                useCache: false,
                displayErrors: false,
            })
            .pipe(map((res: any) => res.data ?? res));
    }
}
